import {
  Comparer,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
  Update,
} from "@reduxjs/toolkit";

import { tdsApi } from "../../api";
import {
  BUILD_DROP_ROOT,
  BUILD_NUMBER,
  BUILD_OR_JOB_NUMBER,
  BUILD_SOURCE_SYSTEM_REGEX,
  CORAL_BUILD_REGEX,
  CUSTOM,
  MONO_BUILD_REGEX,
  NON_DIGIT_REGEX,
} from "../../constants/constants";
import { IBlessedTemplate, Replaceable, ReplaceableType, TopologyType } from "../../constants/types";
import { IEnvironment } from "../../utils/environment";
import {
  checkoutTopologySupported,
  createDefaultTopologyName,
  customTopologySupported,
  getReplaceableType,
  isBuildNumber,
  isSourceSystem,
  splitName,
} from "../../utils/TypescriptUtils";
import { isAzureEnvironment } from "../../utils/Utils";
import { uploadLookupFileSuccess, uploadTemplateSuccess } from "../actions/NewTopologyActions";
import { RootState } from "../setupStore";

type SortPredicateFunction = (n: string) => boolean;

/**
 * Array of predicate functions that {@link replaceablesComparer} will use
 * when sorting replaceables.
 */
const ReplaceableSortPredicates: SortPredicateFunction[] = [
  (n) => isSourceSystem(n),
  (n) => isBuildNumber(n),
  (n) => n.includes(BUILD_DROP_ROOT),
  (n) => n.includes("Flavor"),
];

/**
 * A {@link Comparer} used by {@link replaceablesAdapter} to determine how
 * sort replaceables when they're queried via selectors like
 * {@link selectAllReplaceables} or {@link selectAllReplaceableNames}. The comparer
 * will support replaceables in the following ordering:
 *   1. Replaceables that are "Source System" replaceables.
 *   2. Replaceables that are "Build Number" replaceables.
 *   3. Replaceables that are "Build Drop Root" replaceables.
 *   4. Replaceables that are "Flavor" replaceables.
 *
 * For any replaceables that don't meet the above 4 conditions, they are
 * sorted alphabetically.
 *
 * @param a {@link Replaceable} a being sorted
 * @param b {@link Replaceable} b being sorted
 * @returns a number indicating the result of the compare. -1 if a should be
 * in front of b, 0 if the ordering should not change, and 1 if b should be
 * in front of a.
 */
export const replaceablesComparer: Comparer<Replaceable> = (a, b) => {
  for (const predicate of ReplaceableSortPredicates) {
    const predicateA = predicate(a.Name);
    const predicateB = predicate(b.Name);
    if (predicateA && predicateB) continue;
    if (predicateA) return -1;
    if (predicateB) return 1;
  }

  return a.Name.localeCompare(b.Name);
};

/**
 * Replaceables Entity Adapter used to make management of replaceables
 * easier to work with.
 *
 * To learn more about
 *
 */
export const replaceablesAdapter = createEntityAdapter<Replaceable>({
  selectId: (r) => r.Name,
  sortComparer: replaceablesComparer,
});

export type INewTopologyState = {
  replaceablesEntity: EntityState<Replaceable>;
  showReplaceables: boolean;
  selectedBuildNumber: string;
  buildNumbers: string[];
  specifiedTopologyName: string;
  defaultTopologyName: string;
  selectedTopology: TopologyType | undefined;
  environment: IEnvironment | undefined;
  template: IBlessedTemplate | undefined;
};

export const initialNewTopologyState: INewTopologyState = {
  replaceablesEntity: replaceablesAdapter.getInitialState(),
  showReplaceables: false,
  selectedBuildNumber: "",
  buildNumbers: [],
  specifiedTopologyName: "",
  defaultTopologyName: "",
  selectedTopology: undefined,
  environment: undefined,
  template: undefined,
};

const newTopologySlice = createSlice({
  name: "newTopology",
  initialState: initialNewTopologyState,
  reducers: {
    replaceableUpdated(state, action: PayloadAction<Update<Replaceable>>) {
      replaceablesAdapter.updateOne(state.replaceablesEntity, action.payload);
    },
    selectedTopologyUpdated(_, action: PayloadAction<TopologyType>) {
      return {
        ...initialNewTopologyState,
        selectedTopology: action.payload,
      };
    },
    newTopologyStateReset: () => initialNewTopologyState,
    showReplaceablesUpdated(state, action: PayloadAction<boolean>) {
      state.showReplaceables = action.payload;
    },
    selectedBuildNumberUpdated(state, action: PayloadAction<string>) {
      state.selectedBuildNumber = action.payload;

      if (state.selectedTopology) {
        state.defaultTopologyName = createDefaultTopologyName(state.selectedTopology, action.payload);
      }
    },
    buildNumbersCleared(state) {
      state.buildNumbers = [];
    },
    specifiedTopologyNameReset(state) {
      state.specifiedTopologyName = state.defaultTopologyName;
    },
    specifiedTopologyNameUpdated(state, action: PayloadAction<string>) {
      state.specifiedTopologyName = action.payload;
    },
    __replaceableAdded(state, action: PayloadAction<Replaceable>) {
      replaceablesAdapter.addOne(state.replaceablesEntity, action.payload);
    },
    __replaceablesAdded(state, action: PayloadAction<Replaceable[]>) {
      replaceablesAdapter.addMany(state.replaceablesEntity, action.payload);
    },
    requestRegionUpdated(state, action: PayloadAction<number | null>) {
      if (state.selectedTopology) {
        state.selectedTopology.RequestRegionId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadLookupFileSuccess, (state, action) => {
        replaceablesAdapter.setAll(state.replaceablesEntity, action.payload.template.Replaceables);
        state.showReplaceables = true;
      })
      .addCase(uploadTemplateSuccess, (state, action) => {
        replaceablesAdapter.setAll(state.replaceablesEntity, action.payload.template.Replaceables);
        state.showReplaceables = true;
      })
      .addMatcher(tdsApi.endpoints.getBuildNumbers.matchFulfilled, (state, { payload, meta }) => {
        const buildNumbers = payload;
        const topologyType = meta.arg.originalArgs.TopologyType;

        // We want the newer build numbers to appear first in the dropdown
        const sortedBuildNumbers = buildNumbers.sort().reverse();

        // Preserve the current RequestRegionId only if the topology type matches
        // the currently selected topology to avoid corrupting state from out-of-order responses
        const currentRequestRegionId =
          state.selectedTopology?.Id === topologyType.Id
            ? state.selectedTopology?.RequestRegionId
            : topologyType.RequestRegionId;

        // Create new topology object with preserved RequestRegionId 
        state.selectedTopology = {
          ...topologyType,
          RequestRegionId: currentRequestRegionId !== undefined ? currentRequestRegionId : topologyType.RequestRegionId
        };
        state.environment = topologyType.environment;
        state.buildNumbers = [...sortedBuildNumbers];

        // If we receive build numbers, set the first to be the default selected, and update the default Topology name.
        if (buildNumbers.length > 0 && topologyType) {
          state.defaultTopologyName = createDefaultTopologyName(topologyType, buildNumbers[0]);
          state.selectedBuildNumber = buildNumbers[0];
        }

        // If topology support customization, add CUSTOM to the build numbers list
        if (customTopologySupported(topologyType.DeploymentLookupFileName)) {
          state.buildNumbers.push(CUSTOM);
        }

        // If we received no build numbers and the topology supports custom Topologies,
        // then update the state to reflect creating a Custom topology.
        if (state.buildNumbers.length === 1 && state.buildNumbers.includes(CUSTOM)) {
          if (topologyType.DisplayName) {
            state.defaultTopologyName = createDefaultTopologyName(topologyType, CUSTOM);
          }

          state.selectedBuildNumber = CUSTOM;
          state.showReplaceables = true;
        }
      })
      /**
       * This matcher catches if a request to get a Blessed Template was started. Inside the matcher,
       * determine whether the Topology we're getting the blessed template for supports being checked
       * out. If it does not support being checked out, go ahead and update the state to set {@link CUSTOM}
       * as the selected build number so the UI components can render before the request is completed.
       */
      .addMatcher(tdsApi.endpoints.getBlessedTemplate.matchPending, (state, { meta }) => {
        const topologyType = meta.arg.originalArgs.TopologyType;

        if (!checkoutTopologySupported(topologyType.CheckOutLookupFileName)) {
          if (!state.buildNumbers.includes(CUSTOM)) {
            state.buildNumbers.push(CUSTOM);
          }

          state.selectedBuildNumber = CUSTOM;
          state.showReplaceables = true;
          state.defaultTopologyName = createDefaultTopologyName(topologyType, CUSTOM);
        }
      })
      .addMatcher(tdsApi.endpoints.getBlessedTemplate.matchFulfilled, (state, { payload, meta }) => {
        const blessedTemplate = payload;
        const topologyType = meta.arg.originalArgs.TopologyType;
        const environment = topologyType.environment;

        replaceablesAdapter.setAll(state.replaceablesEntity, blessedTemplate.Replaceables);

        state.template = blessedTemplate;

        // Filter build drop root from replaceables if the Blessed Template fetched
        // is for a Cloud Topology.
        if (isAzureEnvironment(environment)) {
          replaceablesAdapter.removeOne(state.replaceablesEntity, BUILD_DROP_ROOT);
        }
      });
  },
});

export const {
  replaceableUpdated,
  selectedTopologyUpdated,
  newTopologyStateReset,
  showReplaceablesUpdated,
  selectedBuildNumberUpdated,
  buildNumbersCleared,
  specifiedTopologyNameReset,
  specifiedTopologyNameUpdated,
  __replaceableAdded,
  __replaceablesAdded,
  requestRegionUpdated,
} = newTopologySlice.actions;

export default newTopologySlice.reducer;

/**
 * Object containing various selectors automatically created by the {@link replaceablesAdapter},
 * an Entity Adapter produced by RTK-Query.
 *
 * To learn more, visit {@link https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions| the Documentation}
 */
const replaceablesSelectors = replaceablesAdapter.getSelectors<RootState>(
  (state) => state.newTopology.replaceablesEntity,
);

/**
 * Selector that returns the {@link Replaceable} object for a Replaceable
 * in the store if it exists, otherwise it returns undefined.
 */
export const selectReplaceableByName = replaceablesSelectors.selectById;

/**
 * Selector that returns all replaceables for the currently selected Topology
 * Type,with their current values. A replaceable's value is null if the user
 * has not provided a value.
 */
export const selectAllReplaceables = replaceablesSelectors.selectAll;

/**
 * Selector that selects the name of all replaceables for the currently
 * selected Topology Type.
 */
export const selectAllReplaceableNames = replaceablesSelectors.selectIds;

/**
 * Custom selector that returns the list of Topology Types fetched
 * from the TDS API that are available for checkout.
 * @param state The root redux state
 * @returns array of Topology Types available for checkout/creation
 */
export const selectTopologyTypes = (state: RootState): TopologyType[] => state.topologyTypes;

/**
 * Custom selector that returns the selected Topology Type if one has been
 * selected, otherwise it returns undefined.
 *
 * @param state The root redux state
 * @returns the selected {@link TopologyType} or undefined
 */
export const selectSelectedTopology = (state: RootState) => state.newTopology.selectedTopology;

/**
 * Custom selector that, if one exists, returns the other {@link TopologyType} instance of
 * the currently selected Topology. Meaning, if the currently selected Topology is "Datacenter Basic"
 * for the Classic environment, and there exists a "Datacenter Basic" in the Cloud Environment, then
 * the selector will return the instance for the Cloud environment. If no such alternative exists,
 * then the selector returns undefined.
 */
export const selectOtherEnvironmentVersionOfSelectedTopology = createSelector(
  [selectTopologyTypes, selectSelectedTopology],
  (topologies: TopologyType[], selectedTopology: TopologyType | undefined) => {
    // Catch if there is no selectedTopology
    if (!selectedTopology) return undefined;

    // Filter to topologies matching display name
    const matchingTopologies = topologies.filter(
      (t) => t.DisplayName === selectedTopology.DisplayName && t.environment !== selectedTopology.environment,
    );

    if (matchingTopologies.length === 0) return undefined;

    // If we have more than one matching topologies, it means the TDS site is operating with multiple environments,
    // which is currently not supported, so for safety we'll just take the first index.
    return matchingTopologies[0];
  },
);

export const extractReplaceablePrefix = (replaceable: Replaceable): string => {
  // Handle SourceSystem replaceable
  const replaceableName = replaceable.Name;
  if (isSourceSystem(replaceableName)) {
    return replaceableName.replace(BUILD_SOURCE_SYSTEM_REGEX, "");
  }
  if (isBuildNumber(replaceableName)) {
    return replaceableName.replace(BUILD_NUMBER, "").replace(BUILD_OR_JOB_NUMBER, "");
  }

  return "";
};

/**
 * This selector is used to determine if there exists a Build Replaceable that can be associated
 * with the input replaceable.
 *
 * @param replaceables the replaceables to select from
 * @param originalReplaceable the replaceable to find the associated replaceable type for
 * @param targetReplaceableType the type of the replaceable to find that associates with the input replaceable
 * @returns associated replaceable, if one exists
 */
export const selectAssociatedBuildReplaceable = (
  replaceables: Replaceable[],
  originalReplaceable: Replaceable,
  targetReplaceableType: Exclude<ReplaceableType, "StandardReplaceable" | "FlavorReplaceable">,
) => {
  const originalReplaceablePrefix = extractReplaceablePrefix(originalReplaceable);

  switch (targetReplaceableType) {
    case "BuildNumberReplaceable":
      if (!isSourceSystem(originalReplaceable.Name)) return undefined;
      break;
    case "BuildSourceSystemReplaceable":
      if (!isBuildNumber(originalReplaceable.Name)) return undefined;
      break;
  }

  const directlyAssociatedSourceSystemReplaceable = replaceables.find(
    (r) => getReplaceableType(r) === targetReplaceableType && extractReplaceablePrefix(r) === originalReplaceablePrefix,
  );

  if (directlyAssociatedSourceSystemReplaceable) {
    return directlyAssociatedSourceSystemReplaceable;
  }

  const onlyOneBuildNumberReplaceable = replaceables.filter((r) => isBuildNumber(r.Name)).length === 1;
  const onlyOneBuildSourceSystemReplaceable = replaceables.filter((r) => isSourceSystem(r.Name)).length === 1;

  const findIndirectlyAssociatedReplaceable = (r: Replaceable): boolean => {
    const typeMatches = getReplaceableType(r) === targetReplaceableType;

    if (!typeMatches) return false;

    if (targetReplaceableType === "BuildNumberReplaceable") {
      // If we're targeting a build number replaceable, we only want to return 'r' if the original replaceable
      // we're searching on behalf of has no prefix
      return !extractReplaceablePrefix(originalReplaceable);
    } else if (targetReplaceableType === "BuildSourceSystemReplaceable") {
      // If we're targeting a build source system replaceable, we only want to return r if it has no prefix.
      return !extractReplaceablePrefix(r);
    }

    return true;
  };

  const indirectlyAssociatedReplaceable = replaceables.find(findIndirectlyAssociatedReplaceable);

  if (indirectlyAssociatedReplaceable && onlyOneBuildNumberReplaceable && onlyOneBuildSourceSystemReplaceable) {
    return indirectlyAssociatedReplaceable;
  }

  return undefined;
};

/**
 * This function determines if, for a given replaceable, whether we need to give that replaceable a different name on TDS Web.
 *
 * @param replaceables all the replaceables
 * @param originalReplaceable the replaceable that we're checking to see if it needs a new display name
 * @returns the alternative display name, if one is necessary
 */
export const determineAlternativeReplaceableName = (replaceables: Replaceable[], originalReplaceable: Replaceable) => {
  const originalReplaceableName = originalReplaceable.Name;
  const originalReplaceablePrefix = extractReplaceablePrefix(originalReplaceable);

  if (isSourceSystem(originalReplaceableName)) {
    const associatedBuildNumberReplaceable = selectAssociatedBuildReplaceable(
      replaceables,
      originalReplaceable,
      "BuildNumberReplaceable",
    );

    if (associatedBuildNumberReplaceable) {
      if (originalReplaceablePrefix) {
        // If the original replaceable has a prefix, and we have an associated build number replaceable, then we know
        // that the prefix already matches, and we don't need to modify the replaceable name.
        return undefined;
      } else {
        // If it does not have a prefix, however, return a new replaceable name with the prefix of the dynamically
        // associated build number
        return extractReplaceablePrefix(associatedBuildNumberReplaceable) + originalReplaceableName;
      }
    }

    return undefined;
  }

  const associatedSourceSystemReplaceable = selectAssociatedBuildReplaceable(
    replaceables,
    originalReplaceable,
    "BuildSourceSystemReplaceable",
  );

  if (!associatedSourceSystemReplaceable) return undefined;

  // Decide whether to change replaceable name to PipelineBuildID, FocusJobID or BuildNumber
  if (associatedSourceSystemReplaceable.Value === "Focus") {
    return originalReplaceablePrefix + "FocusJobID";
  } else if (associatedSourceSystemReplaceable.Value === "CloudTest") {
    return originalReplaceablePrefix + "PipelineBuildID";
  } else if (associatedSourceSystemReplaceable.Value === "Official") {
    return originalReplaceablePrefix + "BuildNumber";
  } else {
    return undefined;
  }
};

/**
 * This selector is used to determine if a given replaceable needs to have a modified replaceable name. This logic primarily
 * affects Build Number replaceables and Build Source System replaceables.
 */
export const selectAlternativeReplaceableName = createSelector(
  [selectAllReplaceables, (_: RootState, originalReplaceable: Replaceable) => originalReplaceable],
  (replaceables: Replaceable[], originalReplaceable: Replaceable) => {
    if (replaceables.length === 0) return undefined;

    return determineAlternativeReplaceableName(replaceables, originalReplaceable);
  },
);

/**
 * Function used to determine if there is an error for a replaceable. The logic right
 * now just validates the input for Build Number replaceables based on the selected
 * build source system.
 *
 * @param replaceables all replaceables for a Topology
 * @param targetReplaceable the target replaceable to check
 * @returns an error message if one is necessary
 */
const determineReplaceableErrorMessage = (replaceables: Replaceable[], targetReplaceable: Replaceable) => {
  if (!targetReplaceable.Value) return undefined;

  const matchingBuildSourceSystem = selectAssociatedBuildReplaceable(
    replaceables,
    targetReplaceable,
    "BuildSourceSystemReplaceable",
  );

  if (!matchingBuildSourceSystem) return undefined;

  const alternativeBuildSourceSystemName = determineAlternativeReplaceableName(replaceables, matchingBuildSourceSystem);
  const correctedBuildSourceSystemName = splitName(alternativeBuildSourceSystemName ?? matchingBuildSourceSystem.Name);

  if (matchingBuildSourceSystem.Value === "Focus") {
    if (targetReplaceable.Value.includes(".")) {
      return `Focus Job IDs do not contain periods. Did you mean to select 'Official' for the '${correctedBuildSourceSystemName}' value?`;
    }

    if (isNaN(parseInt(targetReplaceable.Value)) || NON_DIGIT_REGEX.test(targetReplaceable.Value)) {
      return "Focus Job IDs only include numbers and no other special characters or Redmond share path information.";
    }

    return undefined;

  } else if (matchingBuildSourceSystem.Value === "CloudTest") {
    if (targetReplaceable.Value.includes(".")) {
      return `ADO Pipeline Build IDs do not contain periods. Did you mean to select 'Official' for the '${correctedBuildSourceSystemName}' value?`;
    }

    if (isNaN(parseInt(targetReplaceable.Value)) || NON_DIGIT_REGEX.test(targetReplaceable.Value)) {
      return "ADO Pipeline Build IDs only include numbers and no other special characters.";
    }

    return undefined;

  } else if (matchingBuildSourceSystem.Value === "Official") {

    if (targetReplaceable.Name.includes("JobNumber")) {

      if (correctedBuildSourceSystemName.includes("Micro")) {

        if (!CORAL_BUILD_REGEX.test(targetReplaceable.Value)) {
          return "Micro (Coral) repositories must specify a complete build number using integers for version numbers separated by dots (no leading 0's), e.g., '17.0.1029.0'.";
        } else {

          return undefined;
        }
      }

      if (correctedBuildSourceSystemName.includes("Search")) {

        if (!CORAL_BUILD_REGEX.test(targetReplaceable.Value)) {
          return "Search repositories must specify a complete build number using integers for version numbers separated by dots (no leading 0's), e.g., '17.0.1029.0'.";
        } else {

          return undefined;
        }
      }

      if (!MONO_BUILD_REGEX.test(targetReplaceable.Value)) {
        return "Official Build Numbers consist of numbers only separated by dots. Mono-Repositories, e.g., Substrate, ControlPlane and ExchangeENT, must prefix version numbers with leading 0's to satisfy the format 'nn.nn.nnnn.nnn', e.g., '15.20.0024.001'.";
      }

      return undefined;
    }

    return undefined;

  } else {

    return undefined;
  }
};

/**
 * Returns an error message if Focus or CloudTest is selected for Build Source System
 * and a period is found in the value for the Build Number input.
 *
 * @example
 * Build Source System --> Focus
 * Bothell Build Number --> 1.2.3
 * Returns an error message
 */
export const selectReplaceableErrorMessage = createSelector(
  [selectAllReplaceables, (_: RootState, targetReplaceable: Replaceable) => targetReplaceable],
  (replaceables: Replaceable[], targetReplaceable: Replaceable) => {
    if (replaceables.length === 0) return undefined;

    return determineReplaceableErrorMessage(replaceables, targetReplaceable);
  },
);

/**
 * Selects the display name of all replaceables with errors.
 */
export const selectAllReplaceablesWithErrors = createSelector(
  [selectAllReplaceables],
  (replaceables: Replaceable[]) => {
    const replacealbeErrors: string[] = [];

    replaceables.forEach((replaceable) => {
      const error = determineReplaceableErrorMessage(replaceables, replaceable);
      if (error) {
        const name = determineAlternativeReplaceableName(replaceables, replaceable);
        replacealbeErrors.push(splitName(name ?? replaceable.Name));
      }
    });

    return replacealbeErrors;
  },
);

/**
 * Selectors for user region stage
 */
export const selectUserRegion = (state: RootState) => state.userRegion.data;
export const selectIsLoadingUserRegion = (state: RootState) => state.userRegion.isLoading;
export const selectUserRegionError = (state: RootState) => state.userRegion.error;
export const selectUserRegionResponseBody = (state: RootState) => state.userRegion.responseBody;
